import React, { useState } from "react";
import { FaGlobe, FaExternalLinkAlt, FaCheck, FaUsers, FaCode, FaChartLine } from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import SEO from '../components/SEO';
import PageHero from '../components/page/PageHero';
import SectionHeading from '../components/page/SectionHeading';
import ProofStrip from '../components/page/ProofStrip';
import heroImage from '../assets/businessWebsites/hero-storefront.webp';
import craftImage from '../assets/businessWebsites/craft-detail.webp';
import reachImage from '../assets/businessWebsites/reach-valley.webp';

/**
 * /business-websites
 *
 * Rebuilt onto the shared page kit. Three things were wrong here beyond the
 * styling, and are fixed rather than restyled:
 *
 *   1. Per-client colours were written as `bg-${client.color}-900/40`. Tailwind
 *      resolves classes at build time by scanning source text, so an
 *      interpolated class name is never generated — those classes did not
 *      exist. The page compensated with ~8 copies of an inline-style ternary.
 *      Both are gone: one accent (moss), no interpolation, no inline colour.
 *   2. The CTA was <a href="/contact">, which drops out of the SPA and reloads
 *      the whole bundle. It is a <Link> now.
 *   3. A render-time console.log fired on every render.
 */
const BusinessWebsites = () => {
    const { t } = useTranslation();
    const [activeClient, setActiveClient] = useState(0);

    const primaryTextColor = "text-silver-light";
    const secondaryTextColor = "text-silver-medium";

    const revealOpts = { triggerOnce: true, threshold: 0.15, rootMargin: "-50px" };
    const [featuresRef, featuresInView] = useInView(revealOpts);
    const [galleryRef, galleryInView] = useInView(revealOpts);

    const businessFeatures = [
        { icon: <FaGlobe className="text-3xl" />, key: "professional" },
        { icon: <FaCode className="text-3xl" />, key: "custom" },
        { icon: <FaChartLine className="text-3xl" />, key: "seo" },
        { icon: <FaUsers className="text-3xl" />, key: "userExperience" },
    ];

    // Real, live client sites. Order is deliberate: Arixa is the flagship.
    const clients = [
        {
            key: "arixa",
            url: "https://app.arixa.io",
            features: ["streaming", "crossPlatform", "modernStack", "scalable"],
        },
        {
            key: "basemint",
            url: "https://basemintstudios.com",
            features: ["responsive", "branding", "seo", "performance"],
        },
        {
            key: "axialis",
            url: "https://www.axialis.hr",
            features: ["responsive", "branding", "seo", "performance", "forms", "cms"],
        },
    ];

    const client = clients[activeClient];

    return (
        <div className="bg-black font-sans min-h-screen">
            <SEO
                title="Business Website Development | Custom Website Developer for Business"
                description="Business website development focused on rankings, conversion, and long-term trust. We build custom websites for businesses that need speed, SEO structure, and happy customers."
                keywords="business website development, custom website developer, website developer for business, custom websites for small business, custom web development services, web developer Croatia, web developer Zadar, seo optimized website"
                url="/business-websites"
            />

            <PageHero
                eyebrow={t('businessWebsites.hero.eyebrow', 'Business websites')}
                title={t('businessWebsites.hero.title')}
                subtitle={t('businessWebsites.hero.subtitle')}
                ctaLabel={t('businessWebsites.hero.cta', 'Book a free 30-minute consultation')}
                ctaTo="/contact"
                secondaryLabel={t('businessWebsites.hero.secondary', 'See client sites')}
                secondaryHref="#client-work"
                proofLine={t('businessWebsites.hero.proof', 'Rated 5.0 on Google · Free consultation · Fixed price agreed before work starts')}
                backgroundImage={heroImage}
            />

            <ProofStrip />

            {/* What you get */}
            <section ref={featuresRef} className="py-20 bg-black">
                <div className="container mx-auto px-4">
                    <SectionHeading
                        eyebrow={t('businessWebsites.features.eyebrow', 'What you get')}
                        title={t('businessWebsites.features.title')}
                        className="mb-12"
                    />
                    <div className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {businessFeatures.map((feature, index) => (
                            <div
                                key={feature.key}
                                className={`rounded-lg border border-gray-800 bg-off-black p-7 transition-all duration-1000 transform hover:border-moss/50 ${featuresInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <div className="mb-4 text-moss-light">{feature.icon}</div>
                                <h3 className={`mb-3 text-lg font-medium ${primaryTextColor}`}>
                                    {t(`businessWebsites.features.${feature.key}.title`)}
                                </h3>
                                <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                                    {t(`businessWebsites.features.${feature.key}.description`)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Craft band — a breath between the two dense sections. */}
            <section className="relative overflow-hidden border-y border-moss-deep/40">
                <img
                    src={craftImage}
                    alt=""
                    aria-hidden="true"
                    className="h-64 w-full object-cover md:h-80"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/30" />
                <div className="absolute inset-0 flex items-center">
                    <div className="container mx-auto px-4">
                        <p className="max-w-xl text-xl md:text-2xl font-serif text-silver-light leading-snug">
                            {t('businessWebsites.craft.line', 'Built by the engineer who writes the code — not handed down a production line.')}
                        </p>
                    </div>
                </div>
            </section>

            {/* Client work */}
            <section id="client-work" ref={galleryRef} className="scroll-mt-24 py-20 bg-black">
                <div className="container mx-auto px-4">
                    <SectionHeading
                        eyebrow={t('businessWebsites.gallery.eyebrow', 'Client work')}
                        title={t('businessWebsites.gallery.title')}
                        className="mb-10"
                    />

                    <div
                        className={`mx-auto max-w-6xl transition-all duration-1000 transform ${galleryInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                    >
                        {/* Client tabs. One accent, and aria-pressed so the
                            selected tab is not communicated by colour alone. */}
                        <div className="mb-10 flex flex-wrap justify-center gap-3">
                            {clients.map((c, index) => (
                                <button
                                    key={c.key}
                                    type="button"
                                    onClick={() => setActiveClient(index)}
                                    aria-pressed={activeClient === index}
                                    className={`rounded-md border px-6 py-3 text-sm font-medium transition-colors duration-300 ${activeClient === index
                                        ? "border-moss bg-moss-deep/40 text-moss-light"
                                        : `border-gray-700 bg-off-black ${secondaryTextColor} hover:border-moss/50 hover:text-silver-light`
                                        }`}
                                >
                                    {t(`businessWebsites.clients.${c.key}.name`)}
                                </button>
                            ))}
                        </div>

                        <div className="rounded-lg border border-gray-800 bg-off-black p-8 md:p-12">
                            <div className="flex flex-col gap-10 lg:flex-row">
                                <div className="flex-grow">
                                    <span className="mb-5 inline-block rounded-full border border-moss/50 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-moss-light">
                                        {t(`businessWebsites.clients.${client.key}.badge`)}
                                    </span>
                                    <h3 className={`mb-4 text-3xl font-serif font-medium ${primaryTextColor}`}>
                                        {t(`businessWebsites.clients.${client.key}.title`)}
                                    </h3>
                                    <p className={`mb-8 text-lg leading-relaxed ${secondaryTextColor}`}>
                                        {t(`businessWebsites.clients.${client.key}.description`)}
                                    </p>

                                    <ul className="mb-8 grid grid-cols-1 gap-3 md:grid-cols-2">
                                        {client.features.map((feature) => (
                                            <li key={feature} className="flex items-center gap-3">
                                                <FaCheck className="flex-shrink-0 text-sm text-moss" aria-hidden="true" />
                                                <span className={`${secondaryTextColor} text-sm`}>
                                                    {t(`businessWebsites.clients.${client.key}.features.${feature}`)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>

                                    <a
                                        href={client.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group inline-flex items-center gap-3 rounded-md border border-moss/60 px-7 py-3 font-medium text-silver-light transition-colors duration-300 hover:border-moss hover:bg-moss hover:text-black"
                                    >
                                        <FaExternalLinkAlt className="transition-transform duration-300 group-hover:scale-110" />
                                        <span>{t(`businessWebsites.clients.${client.key}.visitButton`)}</span>
                                    </a>
                                </div>

                                {/* Project note, not a customer quote. These lines are
                                    not attributed to a named person, and one of them is
                                    written in our own voice about the product — so they
                                    are presented as our summary of the build rather than
                                    dressed up as a testimonial. Real named reviews live
                                    in <ProofStrip /> above. */}
                                <aside className="flex-shrink-0 lg:w-1/3">
                                    <div className="rounded-lg border border-moss-deep/50 bg-moss-night p-8">
                                        <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.2em] text-moss-light">
                                            {t('businessWebsites.projectNote.label', 'About this build')}
                                        </p>
                                        <h4 className={`mb-3 text-xl font-serif font-medium ${primaryTextColor}`}>
                                            {t(`businessWebsites.clients.${client.key}.testimonial.title`)}
                                        </h4>
                                        <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                                            {t(`businessWebsites.clients.${client.key}.testimonial.quote`)}
                                        </p>
                                    </div>
                                </aside>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Closing CTA over the reach band */}
            <section className="relative overflow-hidden">
                <img
                    src={reachImage}
                    alt=""
                    aria-hidden="true"
                    className="h-[26rem] w-full object-cover"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-black/70" />
                <div className="absolute inset-0 flex items-center">
                    <div className="container mx-auto px-4 text-center">
                        <h2 className={`mb-4 text-3xl md:text-4xl font-serif font-medium ${primaryTextColor}`}>
                            {t('businessWebsites.cta.title')}
                        </h2>
                        <p className={`mx-auto mb-8 max-w-2xl ${secondaryTextColor}`}>
                            {t('businessWebsites.cta.description')}
                        </p>
                        <Link
                            to="/contact"
                            className="inline-block rounded-md bg-moss px-8 py-3 text-lg font-medium text-black transition-colors duration-300 hover:bg-moss-light"
                        >
                            {t('businessWebsites.cta.button')}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BusinessWebsites;
